import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { likeContains } from './frontdoor-v3-execution.service';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/**
 * An agent as the v3 API lists it: the agent's own fields, plus the Ziti status the service reads
 * at request time.
 *
 * That status is resolved per page, so a listing carries it on every row. When Ziti cannot be
 * reached the service still answers, with the agent alone and no status — see {@link agentStatus}.
 */
export interface AgentV3 {
    [key: string]: any;

    id: string;
    frontdoorId?: string;
    name?: string;
    description?: string;
    /** Id of the Ziti identity backing the agent. Written once the identity exists. */
    zitiId?: string;
    /**
     * Where the host stands, read from Ziti at request time and named by the service's own enum:
     * `PENDING`, `ENROLLED`, `ONLINE` or `OFFLINE`. Absent when Ziti did not answer, which is not
     * a statement about the agent. Read it through {@link agentStatus} rather than directly.
     */
    status?: string;
    /**
     * The one-time token the agent presents to the Ziti controller. Gone once the agent enrolls,
     * and withheld from a caller who holds `read` but not `update` — it is a credential.
     */
    enrollmentJwt?: string;
    /** When the outstanding enrollment expires. Absent on an enrolled agent. */
    enrollmentExpiresAt?: string;
    /**
     * The short code the agent host trades for {@link enrollmentJwt} at the open enrollment
     * endpoint. Withheld from a caller who holds `read` but not `update`, like the token itself,
     * because whoever holds it can enroll as this agent.
     *
     * It carries no expiry of its own. {@link enrollmentExpiresAt} is when it stops working, and a
     * reissue replaces it.
     */
    registrationCode?: string;
    /** How often the code has handed the token out. A diagnostic — nothing is refused on it. */
    registrationAttempts?: number;
    /** What the host said after it tried to enroll: `success` or `failed`. Absent until it reports. */
    enrollmentReportStatus?: string;
    /** Why the host said the enrollment failed. */
    enrollmentReportReason?: string;
    /** When the host reported the outcome. */
    enrollmentReportedAt?: string;
    createdAt?: string;
    updatedAt?: string;
}

/** A request to add an agent. The service creates the Ziti identity that backs it. */
export interface CreateAgentV3 {
    frontdoorId: string;
    name: string;
    description?: string;
}

/**
 * A request to change an agent. A rename also renames the backing Ziti identity.
 *
 * `description` is omission-tolerant on the wire: leave it out and the stored value stands, send
 * it as `null` and the column is cleared.
 */
export interface UpdateAgentV3 {
    name?: string;
    description?: string | null;
}

/** One page of agents, plus the link that gets the next one. */
export interface AgentPageV3 {
    agents: AgentV3[];
    /** The service's own link to the next page, or '' at the end. Opaque — nothing here parses it. */
    nextHref: string;
    /** Total across all pages when the service reports one, otherwise null. */
    total: number | null;
}

/** The longest name the service accepts, from ZitiUtil.MAX_AGENT_NAME_LENGTH. */
export const MAX_AGENT_NAME_LENGTH = 168;

/** Enough agents to fill a picker without paging it. The API caps a page at its own maximum. */
export const AGENT_PICKER_PAGE_SIZE = 200;

export const DEFAULT_AGENT_PAGE_SIZE = 50;

/** The most agent ids one read names, so the request URL stays short. */
const AGENT_ID_BATCH_SIZE = 50;

/** The query parameter on the Agents page that opens the detail drawer on one agent id. */
export const FD_AGENT_QUERY_PARAM = 'agent';

/** What an agent listing can be narrowed by at the service. Only what `FindAgent` binds. */
export interface AgentListFilters {
    /**
     * Matched as a contains, case-insensitively — one value is read as a `LIKE`, so this is a
     * search box's value and not an exact name.
     */
    name?: string;
    /**
     * The service's own status names: `PENDING`, `ENROLLED`, `ONLINE` or `OFFLINE`. The service reads them from Ziti
     * before the query runs, and answers 503 when it cannot reach Ziti.
     */
    statuses?: string[];
    /** Exact agent ids. An empty list is no filter, so it lists every agent. */
    ids?: string[];
}

/** Adds the filters a listing was asked for. An absent or empty one is left off entirely. */
export function withAgentFilters(params: HttpParams, filters: AgentListFilters = {}): HttpParams {
    const name = (filters.name ?? '').trim();
    let withFilters = name.length > 0 ? params.set('name', likeContains(name)) : params;
    // Repeated rather than joined, because each parameter binds one value of a set.
    for (const status of filters.statuses ?? []) {
        if (status) {
            withFilters = withFilters.append('status', status);
        }
    }
    for (const id of filters.ids ?? []) {
        if (id) {
            withFilters = withFilters.append('id', id);
        }
    }
    return withFilters;
}

/** The service's name for `status`, or '' for `unknown`, which the service does not store. */
export function agentWireStatus(status: AgentStatus): string {
    return status === 'unknown' ? '' : status.toUpperCase();
}

/**
 * Where an agent host stands. Four states come from the service, and `unknown` is this end of the
 * wire: the service answers with the agent alone when it could not reach Ziti, and calling that
 * `pending` would report a live host as un-enrolled.
 *
 * It is one sequence rather than two dimensions. A host holds its token until it enrolls, and only
 * past that point does its edge-router connection say anything — `enrolled` is the service
 * reaching Ziti and Ziti not saying whether the host is connected.
 */
export type AgentStatus = 'pending' | 'enrolled' | 'online' | 'offline' | 'unknown';

const AGENT_STATUSES: AgentStatus[] = ['pending', 'enrolled', 'online', 'offline'];

/**
 * Read the status off a listed agent. Anything the service did not send, or sent under a name
 * this does not know, is `unknown` — a state added at the far end should read as "not known here"
 * rather than as one of the four this does know.
 */
export function agentStatus(agent: AgentV3 | undefined): AgentStatus {
    const status = (agent?.status ?? '').toLowerCase() as AgentStatus;
    return AGENT_STATUSES.includes(status) ? status : 'unknown';
}

/** How a status reads on screen: the badge tone that carries it, the words in it, and why. */
export interface AgentStatusBadge {
    tone: 'ok' | 'warn' | 'err' | 'muted';
    label: string;
    /** What the badge does not say on its face, for a tooltip. '' where the label is the whole of it. */
    hint: string;
}

const AGENT_STATUS_BADGES: Record<AgentStatus, AgentStatusBadge> = {
    online: { tone: 'ok', label: 'Online', hint: '' },
    // Offline is a neutral "not connected" state, not an error, so it reads grey (muted); only a failed
    // operation is red. Pending is still on its way, so it reads amber (warn).
    offline: { tone: 'muted', label: 'Offline', hint: '' },
    enrolled: { tone: 'muted', label: 'Enrolled', hint: 'Ziti did not say whether the host is connected' },
    pending: { tone: 'warn', label: 'Pending', hint: '' },
    unknown: { tone: 'muted', label: 'Unknown', hint: 'The Ziti network did not answer' },
};

/** How to draw `status`. One place, because the listing and every picker must not disagree. */
export function agentStatusBadge(status: AgentStatus): AgentStatusBadge {
    return AGENT_STATUS_BADGES[status] ?? AGENT_STATUS_BADGES.unknown;
}

/**
 * True once the host has used its enrollment, whatever its connection says afterwards. This is
 * what decides that a token is spent and that a reissue un-enrolls a live host, so `offline` has
 * to count: a host that is enrolled and down has still spent its enrollment.
 */
export function agentHasEnrolled(status: AgentStatus): boolean {
    return status === 'enrolled' || status === 'online' || status === 'offline';
}

/**
 * True once an agent's registration code redeems nothing: the host reported a success, or it has
 * enrolled. The row keeps the code either way, so a caller that shows it must check this first.
 */
export function agentCodeSpent(agent: AgentV3 | undefined, status: AgentStatus): boolean {
    return agent?.enrollmentReportStatus === 'success' || agentHasEnrolled(status);
}

/**
 * Reads and writes the agents of one v3 frontdoor. Every path is scoped by the frontdoor id, so
 * the caller has to have chosen one before any of this is worth calling.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3AgentService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /**
     * The agents of `frontdoorId`, by name. The API filters the list to what the caller may read,
     * so a caller with no grant is answered with an empty page rather than a refusal.
     */
    public findAgents(frontdoorId: string, size = AGENT_PICKER_PAGE_SIZE): Observable<AgentV3[]> {
        return this.findAgentPage(frontdoorId, 'name,ASC', size).pipe(map((page) => page.agents));
    }

    /**
     * The agents of `frontdoorId` with these ids, such as the hosts of one share, read in batches that each fit one page
     * and a short URL. No ids answers no agents without a read, because the unfiltered listing returns every agent. A
     * deleted or unreadable agent is left out.
     */
    public findAgentsByIds(frontdoorId: string, ids: string[]): Observable<AgentV3[]> {
        const wanted = [...new Set(ids.filter((id) => !!id))];
        if (!wanted.length) {
            return of([]);
        }
        const batches: string[][] = [];
        for (let i = 0; i < wanted.length; i += AGENT_ID_BATCH_SIZE) {
            batches.push(wanted.slice(i, i + AGENT_ID_BATCH_SIZE));
        }
        return forkJoin(
            batches.map((batch) => {
                const params = withAgentFilters(new HttpParams().set('size', String(batch.length)), { ids: batch });
                return this.http
                    .get(this.agentsUrl(frontdoorId), { headers: this.headers(), params })
                    .pipe(map((response: any) => this.readPage(response).agents));
            })
        ).pipe(map((pages) => pages.flat()));
    }

    /**
     * One page of `frontdoorId`'s agents, newest first unless `sort` says otherwise. The service pages by offset, so
     * `page` counts from 0 and the page's `total` counts every match. `filters` narrows the listing at the service.
     */
    public findAgentPage(
        frontdoorId: string,
        sort = 'createdAt,DESC',
        size = DEFAULT_AGENT_PAGE_SIZE,
        filters: AgentListFilters = {},
        page = 0
    ): Observable<AgentPageV3> {
        const params = withAgentFilters(new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page)), filters);
        return this.http
            .get(this.agentsUrl(frontdoorId), { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    /**
     * The next page, by following the link the service returned. Taken verbatim, because the
     * cursor inside it only resumes under the order that made it.
     */
    public followNext(nextHref: string): Observable<AgentPageV3> {
        return this.http
            .get(nextHref, { headers: this.headers() })
            .pipe(map((response: any) => this.readPage(response)));
    }

    /**
     * Adds an agent to `frontdoorId`. The body repeats the id because the service rejects a body
     * that names a different account, and the caller should not be the one to get that wrong.
     *
     * The response carries `enrollmentJwt`, which is the point of creating an agent: it is the
     * one-time token the host enrolls with, and it is not stored anywhere this can read it back.
     */
    public createAgent(frontdoorId: string, agent: CreateAgentV3): Observable<AgentV3> {
        const body = { ...agent, frontdoorId };
        return this.http
            .post(this.agentsUrl(frontdoorId), body, { headers: this.headers() })
            .pipe(map((response: any) => response as AgentV3));
    }

    /**
     * Whether {@link createAgent} would be accepted, without creating anything.
     *
     * The same verb, path and body - `nf-validate` is the whole of the routing - so this takes
     * the request already built for the write rather than building a second one, because a form
     * that went green on a body nobody was going to send is worse than no check. Authorized
     * identically, so a caller without create permission still gets a 403.
     */
    public validateCreateAgent(frontdoorId: string, agent: CreateAgentV3): Observable<ValidationError[]> {
        const body = { ...agent, frontdoorId };
        return checkFrontdoorBody(this.http, this.agentsUrl(frontdoorId), body);
    }

    /**
     * Whether {@link updateAgent} would be accepted, without changing anything. See
     * {@link validateCreateAgent}.
     *
     * **`PUT`, where the write is a `PATCH`.** The method is what picks the rules - a `PUT` demands
     * every mutable field, a `PATCH` takes what it is given - so this checks the agent as it would
     * end up rather than only the fields the body happens to carry. `name` is the one field that
     * makes required, and the form always sends it.
     *
     * A rename to the agent's own name is a no-op rather than a name that is taken, as it is on
     * the real call.
     */
    public validateUpdateAgent(
        frontdoorId: string,
        agentId: string,
        update: UpdateAgentV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, `${this.agentsUrl(frontdoorId)}/${agentId}`, update, 'PUT');
    }

    /**
     * One agent of `frontdoorId`, with its enrollment state read at request time. An agent
     * addressed under another account answers 404, so a stale link is a miss rather than a read
     * across accounts.
     */
    public getAgent(frontdoorId: string, agentId: string): Observable<AgentV3> {
        return this.http
            .get(`${this.agentsUrl(frontdoorId)}/${agentId}`, { headers: this.headers() })
            .pipe(map((response: any) => response as AgentV3));
    }

    /**
     * Applies `update` to one agent. `PATCH`, so only the fields the body carries are applied and
     * a `description` sent as null clears it.
     */
    public updateAgent(frontdoorId: string, agentId: string, update: UpdateAgentV3): Observable<AgentV3> {
        return this.http
            .patch(`${this.agentsUrl(frontdoorId)}/${agentId}`, update, { headers: this.headers() })
            .pipe(map((response: any) => response as AgentV3));
    }

    /**
     * Issues the agent a fresh enrollment and answers with it. An agent that has not enrolled
     * keeps the token it holds and only its expiry moves out. An agent that has enrolled is
     * un-enrolled: its certificate stops working and its host reaches nothing until it enrolls
     * again with the new token.
     */
    public reissueEnrollment(frontdoorId: string, agentId: string): Observable<AgentV3> {
        return this.http
            .post(`${this.agentsUrl(frontdoorId)}/${agentId}/reissue`, {}, { headers: this.headers() })
            .pipe(map((response: any) => response as AgentV3));
    }

    /** Deletes the agent and the Ziti identity behind it, which un-enrolls its host. */
    public deleteAgent(frontdoorId: string, agentId: string): Observable<any> {
        return this.http.delete(`${this.agentsUrl(frontdoorId)}/${agentId}`, { headers: this.headers() });
    }

    private agentsUrl(frontdoorId: string): string {
        return `${this.apiUrl}/v3/${frontdoorId}/agents`;
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    /** Accepts the HAL collection, a bare array, or a Spring page, so the shape can settle later. */
    private readPage(response: any): AgentPageV3 {
        if (Array.isArray(response)) {
            return { agents: response, nextHref: '', total: response.length };
        }
        const agents = this.readList(response);
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        return { agents, nextHref: response?._links?.next?.href ?? '', total };
    }

    private readList(response: any): AgentV3[] {
        const embedded = response?._embedded;
        if (embedded) {
            const collection = embedded.agents ?? embedded.agentList ?? Object.values(embedded)[0];
            if (Array.isArray(collection)) {
                return collection;
            }
        }
        if (Array.isArray(response?.content)) {
            return response.content;
        }
        this.logger.error('findAgents: unrecognised response shape', response);
        return [];
    }
}
